import { type ReactNode, useMemo } from 'react';

import {
  type AcceptSidekickConsent,
  type EnsureSidekickConversation,
  type GetSidekickConsentStatus,
  type SearchSidekickRecipients,
  SidekickApiContext,
  type SidekickApiContextValue,
  type SubmitSidekickFeedback,
} from './SidekickApiContext';

/** Props for {@link SidekickApiProvider}. */
export type SidekickApiProviderProps = {
  /**
   * Absolute base URL of the Sidekick API service — in `mf-insights`,
   * `${globalThis.appConfig.apiBaseUrl}/sidekick`. Hooks append the
   * conversation path themselves (`/conversations/{id}/...`).
   */
  apiBaseUrl: string;
  /** Optional fleet identifier applied to each message request. */
  fleetId?: string;
  /** Optional host interface identifier used for experiment routing. */
  interfaceName?: string;
  /** Injected callback that ensures an active backend conversation exists. */
  ensureConversation?: EnsureSidekickConversation;
  /** Injected callback that persists assistant-message feedback. */
  submitFeedback?: SubmitSidekickFeedback;
  /**
   * Injected callback that reads the current user's AI-disclaimer consent
   * status. Inject together with `acceptConsent`: the consent gate uses
   * the injected pair only when BOTH are supplied. If only one is given
   * it is ignored and both operations fall back to the localStorage
   * default (see `useConsentApi`).
   */
  getConsentStatus?: GetSidekickConsentStatus;
  /**
   * Injected callback that records the current user's acceptance of the AI
   * disclaimer. Inject together with `getConsentStatus` (all-or-nothing:
   * both, or both fall back to the localStorage default).
   */
  acceptConsent?: AcceptSidekickConsent;
  /**
   * Injected callback that resolves the host's user directory for the
   * share-via-email recipients field. Omit it to leave the field on
   * free-typed addresses with no suggestions.
   */
  searchRecipients?: SearchSidekickRecipients;
  children: ReactNode;
};

/**
 * Provides the Sidekick package's network-bound hooks with the host's
 * API base URL. Hosts integrating via {@link SidekickLayoutShell} get
 * this provider mounted internally — pass `apiBaseUrl` directly on
 * the shell instead of wrapping it yourself. Direct mounting of this
 * provider is only needed for Storybook stories, isolated tests, or
 * surfaces that consume {@link useSidekickApi} outside the shell tree.
 *
 * Decoupled from {@link SidekickConfigProvider} on purpose: i18n /
 * timezone config is read by formatters that run on every render,
 * while API config is read only by the streaming + sync hooks. Keeping
 * them separate lets either context evolve without touching the
 * other's consumers.
 */
export const SidekickApiProvider = ({
  apiBaseUrl,
  fleetId,
  interfaceName,
  ensureConversation,
  submitFeedback,
  getConsentStatus,
  acceptConsent,
  searchRecipients,
  children,
}: SidekickApiProviderProps): JSX.Element => {
  const value = useMemo<SidekickApiContextValue>(
    () => ({
      apiBaseUrl,
      fleetId,
      interfaceName,
      ensureConversation,
      submitFeedback,
      getConsentStatus,
      acceptConsent,
      searchRecipients,
    }),
    [
      apiBaseUrl,
      fleetId,
      interfaceName,
      ensureConversation,
      submitFeedback,
      getConsentStatus,
      acceptConsent,
      searchRecipients,
    ]
  );

  return (
    <SidekickApiContext.Provider value={value}>
      {children}
    </SidekickApiContext.Provider>
  );
};

export default SidekickApiProvider;
